import { useEffect, useId, useState } from "react";
import { Check } from "lucide-react";
import type {
  ClassificationResponse,
  CpcCode,
  SuggestionResponse,
} from "@patent-studio/api-types";
import { IconTile } from "@/components/ui/IconTile";
import { Input } from "@/components/ui/input";
import { MicroLabel } from "@/components/ui/MicroLabel";
import { Panel } from "@/components/ui/Panel";
import { ProgressBar } from "@/components/ui/ProgressBar";
import { Spinner } from "@/components/ui/Spinner";
import { useCpcSearch } from "@/hooks/use-classification";
import { stampOf } from "@/lib/day";
import { plural } from "@/lib/plural";
import { cn } from "@/lib/utils";

/**
 * A CPC title as the page reads it. The scheme marks some words with braces,
 * which say where the text came from, not what it means, so they are dropped.
 */
export function cpcTitle(title: string): string {
  return title.replace(/[{}]/g, "").trim();
}

/** The share of capital letters at which a title reads as written in capitals. */
const MOSTLY_CAPITALS = 0.8;

/**
 * A head of a code's path as the breadcrumb reads it: short, and in sentence
 * case. The scheme's references to other codes, in parentheses, are dropped,
 * and a section's or class's title, which the scheme writes in capitals, reads
 * as a sentence.
 */
export function cpcHeadTitle(title: string): string {
  const short = cpcTitle(title)
    .replace(/\s*\([^()]*\)/g, "")
    .replace(/\s+/g, " ")
    .trim();
  const letters = short.replace(/[^A-Za-z]/g, "");
  const capitals = letters.replace(/[^A-Z]/g, "").length;
  // Written in capitals, bar the odd "e.g.": the scheme's way of writing a section or class.
  if (letters.length === 0 || capitals / letters.length < MOSTLY_CAPITALS) {
    return short;
  }
  const lower = short.toLowerCase();
  return lower.charAt(0).toUpperCase() + lower.slice(1);
}

/** What a row shows beside its code: the evidence of a suggestion, or where a code came from. */
export type RowSource =
  | { kind: "suggested"; suggestion: SuggestionResponse; threshold: number }
  | { kind: "added" };

/**
 * One code the attorney can select: its symbol, title and place in the scheme,
 * and its score and evidence where Praxis suggested it. The whole row is the
 * checkbox, toggled by a click, Space or Enter.
 */
export function CodeRow({
  code,
  source,
  checked,
  onToggle,
  readOnly = false,
  compact = false,
}: {
  code: CpcCode;
  source?: RowSource;
  checked: boolean;
  onToggle?: () => void;
  readOnly?: boolean;
  compact?: boolean;
}) {
  const title = cpcTitle(code.title);
  const path = code.path ?? [];
  const toggle = () => {
    if (!readOnly) onToggle?.();
  };
  return (
    <Panel
      shape="row"
      pad={compact ? "tight" : "snug"}
      tone={checked ? "brand" : undefined}
      role="checkbox"
      aria-checked={checked}
      aria-readonly={readOnly || undefined}
      aria-label={`${code.symbol} ${title}`}
      tabIndex={readOnly ? -1 : 0}
      onClick={toggle}
      onKeyDown={(event) => {
        if (event.key === " " || event.key === "Enter") {
          event.preventDefault();
          toggle();
        }
      }}
      className={cn("flex gap-3", !readOnly && "cursor-pointer")}
    >
      <span
        aria-hidden
        className={cn(
          "mt-0.5 flex size-[18px] shrink-0 items-center justify-center rounded-[5px] border-[1.5px]",
          checked
            ? "border-primary bg-primary text-primary-foreground"
            : "border-border bg-card",
        )}
      >
        {checked && <Check className="size-3" />}
      </span>
      <span className="min-w-0 flex-1">
        <span className="flex items-baseline gap-2">
          <span className="font-mono text-[13px] text-primary">
            {code.symbol}
          </span>
          <span className="text-[13.5px] font-semibold">{title}</span>
        </span>
        {path.length > 0 && (
          <span className="mt-1 block text-[11px] text-muted-foreground">
            {path.map((head, index) => (
              <span key={head.symbol}>
                {index > 0 && <span aria-hidden> › </span>}
                <span className="font-mono">{head.symbol}</span>{" "}
                {cpcHeadTitle(head.title)}
              </span>
            ))}
          </span>
        )}
        <RowEvidence source={source} />
      </span>
      {source?.kind === "suggested" && (
        <RowScore
          score={source.suggestion.score}
          reached={source.suggestion.score >= source.threshold}
          symbol={code.symbol}
        />
      )}
    </Panel>
  );
}

function RowEvidence({ source }: { source?: RowSource }) {
  if (source === undefined) return null;
  if (source.kind === "added") {
    return (
      <span className="mt-2 block text-[12px] text-muted-foreground">
        Added from the full CPC scheme
      </span>
    );
  }
  return (
    <>
      {source.suggestion.evidence.map((passage) => (
        <span
          key={`${passage.file_id}-${String(passage.page)}-${passage.quote}`}
          className="mt-2 block text-[12px]"
        >
          "{passage.quote}"
          <span className="text-muted-foreground">
            {" "}
            · {passage.document_label}
            {passage.cites_pages && `, p. ${String(passage.page)}`}
          </span>
        </span>
      ))}
    </>
  );
}

function RowScore({
  score,
  reached,
  symbol,
}: {
  score: number;
  reached: boolean;
  symbol: string;
}) {
  return (
    <span className="w-[86px] shrink-0 text-right">
      <span
        className={cn(
          "font-mono text-detail font-bold",
          reached && "text-success",
        )}
      >
        {score}%
      </span>
      <ProgressBar
        value={score}
        max={100}
        label={`How strongly the disclosure supports ${symbol}`}
        tone={reached ? "success" : "info"}
        className="mt-1"
      />
    </span>
  );
}

/** A labelled group of rows, read by a screen reader as one group. */
export function CodeGroup({
  title,
  note,
  children,
}: {
  title: string;
  note?: string;
  children: React.ReactNode;
}) {
  const id = useId();
  return (
    <section role="group" aria-labelledby={id} className="mt-6">
      <h2 id={id} className="mb-2 flex items-baseline gap-2">
        <MicroLabel as="span">{title}</MicroLabel>
        {note !== undefined && (
          <span className="text-[11.5px] text-muted-foreground">{note}</span>
        )}
      </h2>
      <div className="space-y-2">{children}</div>
    </section>
  );
}

/** Search the whole scheme by symbol or by the words of a title; ticking a result adds it. */
export function SchemeSearch({
  chosen,
  onAdd,
}: {
  chosen: ReadonlySet<string>;
  onAdd: (code: CpcCode) => void;
}) {
  const [query, setQuery] = useState("");
  const search = useCpcSearch(query);
  const results = search.data?.results ?? [];
  const id = useId();
  return (
    <section className="mt-8">
      <MicroLabel as="label" htmlFor={id} className="mb-2 block">
        Search the full CPC scheme
      </MicroLabel>
      <Input
        id={id}
        type="search"
        aria-label="Search the full CPC scheme"
        placeholder="Search by symbol (H05H, H05H1/02) or by words in the title"
        value={query}
        onChange={(event) => {
          setQuery(event.target.value);
        }}
      />
      {search.isSuccess && results.length === 0 && (
        <p className="mt-2 text-[12px] text-muted-foreground">
          No CPC entries match "{query.trim()}".
        </p>
      )}
      {results.length > 0 && (
        <div className="mt-2 space-y-1.5">
          {results.map((code) => (
            <CodeRow
              key={code.symbol}
              code={code}
              compact
              checked={chosen.has(code.symbol)}
              onToggle={() => {
                onAdd(code);
              }}
            />
          ))}
        </div>
      )}
    </section>
  );
}

/** The steps shown while codes are being suggested, one more done every so often. */
export const RUN_STEPS = [
  "Reading the invention disclosure",
  "Searching CPC titles and definitions",
  "Narrowing through the hierarchy",
  "Ranking candidates against the disclosure",
] as const;

/** How long each step shows as the current one. */
export const RUN_STEP_MS = 850;

export function RunningSteps({ rerun }: { rerun: boolean }) {
  const [current, setCurrent] = useState(0);
  useEffect(() => {
    const timer = window.setInterval(() => {
      setCurrent((step) => Math.min(step + 1, RUN_STEPS.length - 1));
    }, RUN_STEP_MS);
    return () => {
      window.clearInterval(timer);
    };
  }, []);
  return (
    <Panel shape="card" pad="roomy" className="mt-6" role="status">
      <p className="text-[14px] font-semibold">
        {rerun
          ? "Intake material changed. Re-running classification"
          : "Classifying the invention"}
      </p>
      <ol className="mt-3 space-y-2">
        {RUN_STEPS.map((step, index) => (
          <li
            key={step}
            className={cn(
              "flex items-center gap-2 text-[13px]",
              index === current && "font-bold",
              index > current && "text-muted-foreground",
            )}
          >
            {index < current ? (
              <IconTile tone="done" shape="round" size="xs">
                <Check />
              </IconTile>
            ) : index === current ? (
              <Spinner size="sm" />
            ) : (
              <IconTile tone="open" shape="round" size="xs">
                {null}
              </IconTile>
            )}
            {step}
          </li>
        ))}
      </ol>
    </Panel>
  );
}

function listed(symbols: readonly string[]): string {
  return symbols.length === 0 ? "none" : symbols.join(", ");
}

/** The stored record of the attorney's confirmation: who, when, and what changed. */
export function ConfirmedCard({
  reading,
  byYou,
}: {
  reading: ClassificationResponse;
  byYou: boolean;
}) {
  const record = reading.confirmation;
  if (record === null) return null;
  const count = reading.selected.length;
  return (
    <Panel shape="card" pad="roomy" tone="success" className="mt-6">
      <p className="text-[14px] font-semibold">
        {plural(count, "classification")} confirmed
      </p>
      <p className="mt-1 text-detail">
        Confirmed by {byYou ? "you" : record.by} · {stampOf(record.at)}
      </p>
      <p className="mt-1 text-detail">
        Praxis preselected: {listed(record.preselected)}
      </p>
      <p className="mt-1 text-detail">
        You added: {listed(record.added)} · You removed:{" "}
        {listed(record.removed)}
      </p>
      <p className="mt-2 text-detail text-muted-foreground">
        Passed to Thesis, Novelty, Claim Strategy, Specification and generation.
        Unselected suggestions are not.
      </p>
    </Panel>
  );
}
